import { ReactNode } from "react";
import { BaseCategoryType, CategoryTPType, CategoryTreeType, ChildrenCategoryType } from "categories/types";

export type OptionType = {
  id: number;
  value: number;
  label: ReactNode;
  parent?: number;
  isLeaf?: boolean;
  loading?: boolean;
  children?: OptionType[];
};

// Итеративно преобразовывает формат дерева
export const transformDataToCategory = (data: CategoryTreeType[] | ChildrenCategoryType[]): OptionType[] => {
  if (!data || data.length === 0) return [];

  const getParentId = (parent: number | CategoryTPType[]): number | undefined => {
    if (Array.isArray(parent)) {
      return parent.length ? parent[parent.length - 1]?.id : undefined;
    } else {
      return parent;
    }
  };

  const result: OptionType[] = [];
  const stack: (CategoryTreeType | ChildrenCategoryType)[] = [...data];
  const nodeMap: Map<number, OptionType> = new Map<number, OptionType>();

  while (stack.length > 0) {
    const current: CategoryTreeType | ChildrenCategoryType = stack.shift();

    if (!current) continue;

    let node: OptionType;

    if ("children_ids" in current) {
      const { id, name, children_ids, parent } = current as CategoryTreeType;

      // Если уже существует в nodeMap (как пустой узел — добавим данные)
      if (nodeMap.has(id)) {
        node = nodeMap.get(id)!;
        node.label = name;
        node.isLeaf = !children_ids || children_ids.length === 0;
        node.parent = getParentId(parent);
      } else {
        node = {
          id,
          value: id,
          label: name,
          isLeaf: !children_ids || children_ids.length === 0,
          parent: getParentId(parent),
          children: [],
        };

        nodeMap.set(id, node);
      }

      // Обрабатываем детей
      if (children_ids && children_ids.length > 0) {
        for (const child of children_ids) {
          stack.push(child);
        }
      }
    } else {
      const { id, name, has_children, parent } = current as ChildrenCategoryType;

      node = {
        id,
        value: id,
        label: name,
        isLeaf: !has_children,
        parent: getParentId(parent),
        children: undefined,
      };

      nodeMap.set(id, node);
    }
  }

  // Вторая фаза: формируем дерево
  for (const node of nodeMap.values()) {
    if (node.parent !== undefined && nodeMap.has(node.parent)) {
      const parentNode: OptionType = nodeMap.get(node.parent);

      parentNode.children = parentNode.children || []; // Инициализируем children, если их нет
      parentNode.children.push(node);
    } else {
      result.push(node); // Корневые узлы
    }
  }

  return result;
};

export const updateCategory = (categories: OptionType[], id: number, newChildren: OptionType[]): OptionType[] => {
  return categories.map((category: OptionType) => {
    if (category.value === id) {
      return {
        ...newChildren?.[0],
      };
    }

    if (category.children && category.children.length > 0) {
      return {
        ...category,
        children: updateCategory(category.children, id, newChildren),
      };
    }

    return category;
  });
};

// Поиск конкретного узла по id
export const findNodeById = (categories: OptionType[], id: number): OptionType | undefined => {
  if (categories) {
    for (const category of categories) {
      if (category.value === id) {
        return category;
      }

      if (category.children && category.children.length > 0) {
        const foundInChildren = findNodeById(category.children, id);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }
  }

  return undefined;
};

export const buildCategoryTreeFromChain = (chainOptions: OptionType[]): OptionType[] => {
  const map: Map<any, OptionType> = new Map<any, OptionType>();
  const result: OptionType[] = [];

  chainOptions
    .map((item: OptionType) => {
      map.set(item.value, item);
      return item;
    })
    .forEach((item: OptionType) => {
      if (item.parent) {
        if (map.has(item.parent)) {
          const parent: OptionType = map.get(item.parent);
          const existingChildIndex: number = parent.children.findIndex((child) => child.value === item.value);

          if (existingChildIndex !== -1) {
            parent.children[existingChildIndex] = map.get(item.value);
          } else {
            parent.children.push(map.get(item.value));
          }
        }
      } else {
        result.push(map.get(item.value));
      }
    });

  return result;
};

// Обогащение основного дерева недостающими узлами
export const mergeTrees = (bigTree: OptionType[], smallTree: OptionType[]): OptionType[] => {
  // Создаем карту для быстрого доступа к узлам большого дерева
  const bigTreeMap: Map<number, OptionType> = new Map<number, OptionType>();

  const buildMap = (nodes: OptionType[]): void => {
    nodes.forEach((node: OptionType) => {
      bigTreeMap.set(node.id, node);

      if (node.children) {
        buildMap(node.children);
      }
    });
  };

  buildMap(bigTree);

  // Рекурсивная функция для слияния деревьев
  const mergeNode = (bigNode: OptionType, smallNode: OptionType): void => {
    if (smallNode.children && smallNode.children.length > 0) {
      if (!bigNode.children) {
        bigNode.children = [];
      }

      smallNode.children?.forEach((smallRoot: OptionType) => {
        const existingBigChild: OptionType = bigNode.children
          ?.find((child: OptionType) => child.id === smallRoot.id);

        if (!existingBigChild) {
          // Если ребенка нет в большом дереве, добавляем его
          bigNode.children.push({ ...smallRoot });
          bigTreeMap.set(smallRoot.id, { ...smallRoot }); // Обновляем карту
        } else {
          // Если ребенок уже существует, рекурсивно обогащаем его
          mergeNode(existingBigChild, smallRoot);
        }
      });
    }
  };

  // Проходим по всем корням маленького дерева
  smallTree.forEach((smallRoot: OptionType) => {
    if (!bigTreeMap.has(smallRoot.id)) {
      // Если корня нет в большом дереве, добавляем его как новый корень
      bigTree.push({ ...smallRoot });
      bigTreeMap.set(smallRoot.id, { ...smallRoot }); // Обновляем карту
    } else {
      // Если корень уже существует, обогащаем его
      const existingBigRoot: OptionType = bigTreeMap.get(smallRoot.id)!;

      mergeNode(existingBigRoot, smallRoot);
    }
  });

  return bigTree;
};

// Ищет дубликаты узлов и объединяет, оставляя только уникальные узлы
export const mergeDuplicateNodes = (tree: OptionType[]): OptionType[] => {
  const nodeMap: Map<number, OptionType> = new Map<number, OptionType>();

  // Рекурсивная функция для обработки узлов
  const processNode = (node: OptionType): void => {
    if (!nodeMap.has(node.id)) {
      nodeMap.set(node.id, { ...node, children: [] });
    }

    const existingNode: OptionType = nodeMap.get(node.id)!;

    // Объединяем детей
    if (node.children && node.children.length > 0) {
      // Создаем временную карту для уникализации детей
      const childrenMap: Map<number, OptionType> = new Map<number, OptionType>(
        existingNode.children?.map((child: OptionType) => [child.id, child]) || []
      );

      // Добавляем новых детей в карту
      node.children.forEach((child: OptionType) => {
        processNode(child);
        childrenMap.set(child.id, nodeMap.get(child.id)!); // Уникализируем по id
      });

      // Обновляем массив детей
      existingNode.children = Array.from(childrenMap.values());
    }
  };

  // Обрабатываем все узлы в исходном дереве
  tree.forEach(processNode);

  // Возвращаем только корневые узлы
  return Array.from(nodeMap.values())
    .filter((node: OptionType) => node.parent === undefined || node.parent === null);
};

// Собирает ключи для результатов поиска
export const collectExpandedKeys = (nodes: OptionType[]): number[] =>
  collectKeys(nodes, (node: OptionType) => node.children?.length > 0);

// Собирает ключи для выбранной ветки
export const collectTreeKeys = (
  nodes: OptionType[] = [],
  onlyWithChildren: boolean = true
): number[] => collectKeys(nodes, (node: OptionType) => !onlyWithChildren || node.children?.length > 0);

// Собирает ключи для разворачивания узлов дерева категорий
const collectKeys = (
  nodes: OptionType[] = [],
  shouldInclude: (node: OptionType) => boolean = () => true
): number[] => {
  const keys: number[] = [];
  const stack: OptionType[] = [...nodes];

  while (stack.length > 0) {
    const node: OptionType = stack.shift()!;
    const hasChildren: boolean = node.children?.length > 0;

    if (shouldInclude(node)) {
      keys.push(node.id);
    }

    if (hasChildren) {
      stack.push(...node.children!);
    }
  }

  return keys;
};

export const getCategoryParents = (parents: CategoryTPType[]): ChildrenCategoryType[] => parents
  ?.map((parent: CategoryTPType, index: number) => {
    return {
      ...parent,
      parent: index === 0 ? null : parents?.[index - 1]?.id,
      has_children: true
    };
  });

export const generateCategoryChains = (categories: BaseCategoryType[]): ChildrenCategoryType[][] => {
  return categories?.map((category: BaseCategoryType) => {
    const categoryParents: ChildrenCategoryType[] = getCategoryParents(category.parent as CategoryTPType[]);

    return [
      ...categoryParents,
      {
        id: category.id,
        name: category.name,
        parent: categoryParents?.[categoryParents.length - 1]?.id,
        has_children: false,
      }
    ];
  });
};
